import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, ComposedChart, BarChart, Bar, Cell,
  ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, AlertTriangle, Info, Loader2, RefreshCw } from 'lucide-react';

// =============================================================================
// API CLIENT
// =============================================================================

const API_BASE = 'https://ppf-api.ppfpipeline.workers.dev/api';

async function fetchAPI(endpoint) {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch ${endpoint}:`, error);
    throw error;
  }
}

// Fallback mock data generator (used when API is unavailable)
function generateFallbackData() {
  const data = [];
  const startDate = new Date('2020-01-01');
  const scenarios = [
    { months: 3, gY: 0.2, gM: 0.15, gV: -4.0 },
    { months: 3, gY: 0.3, gM: 0.12, gV: -2.0 },
    { months: 6, gY: 0.4, gM: 0.10, gV: 3.0 },
    { months: 6, gY: 0.5, gM: 0.08, gV: 6.0 },
    { months: 6, gY: 0.6, gM: 0.07, gV: 4.0 },
    { months: 6, gY: 0.3, gM: 0.06, gV: -5.0 },
    { months: 6, gY: 0.25, gM: 0.05, gV: -3.0 },
    { months: 6, gY: 0.4, gM: 0.05, gV: 1.0 },
    { months: 6, gY: 0.45, gM: 0.04, gV: 0.5 },
    { months: 6, gY: 0.8, gM: 0.04, gV: 1.5 },
    { months: 6, gY: 1.0, gM: 0.03, gV: 2.0 },
    { months: 6, gY: 0.9, gM: 0.03, gV: 1.0 },
  ];
  
  let monthIndex = 0;
  let cliLevel = 100;
  
  scenarios.forEach(scenario => {
    for (let i = 0; i < scenario.months; i++) {
      const date = new Date(startDate);
      date.setMonth(date.getMonth() + monthIndex);
      
      const noise = () => (Math.random() - 0.5) * 0.3;
      const gY = scenario.gY + noise();
      const gM = scenario.gM + noise() * 0.02;
      const gV = scenario.gV + noise() * 2;
      const ppg = gY - gM - gV;
      
      const velUncertainty = Math.abs(gV) > 3 ? 3.5 : 2.5;
      
      const btcusdChange = (Math.random() - 0.3) * 15;
      cliLevel *= (1 - ppg / 100 + btcusdChange / 100);
      
      let regime = 'uncertain';
      if (ppg - velUncertainty > 0) regime = 'deflation';
      else if (ppg + velUncertainty < 0) regime = 'inflation';
      
      data.push({
        date_month: date.toISOString().slice(0, 7) + '-01',
        dateLabel: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        ppg: Math.round(ppg * 100) / 100,
        ppg_p10: Math.round((ppg - velUncertainty) * 100) / 100,
        ppg_p50: Math.round(ppg * 100) / 100,
        ppg_p90: Math.round((ppg + velUncertainty) * 100) / 100,
        g_y_star: Math.round(gY * 100) / 100,
        g_m: Math.round(gM * 100) / 100,
        g_v: Math.round(gV * 100) / 100,
        cli_level: Math.round(cliLevel * 10) / 10,
        regime_flag: regime,
      });
      monthIndex++;
    }
  });
  return data;
}

// =============================================================================
// CUSTOM HOOKS
// =============================================================================

function usePPFData() {
  const [data, setData] = useState(null);
  const [latest, setLatest] = useState(null);
  const [cliData, setCliData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [historyRes, latestRes, cliRes, metaRes] = await Promise.all([
        fetchAPI('/ppg/history'),
        fetchAPI('/ppg/latest'),
        fetchAPI('/cli/latest'),
        fetchAPI('/meta/model')
      ]);
      
      // Transform history data for charts
      const transformedHistory = historyRes.data.map(d => ({
        ...d,
        dateLabel: new Date(d.date_month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      }));
      
      setData(transformedHistory);
      setLatest(latestRes);
      setCliData(cliRes);
      setMeta(metaRes);
      setUsingFallback(false);
    } catch (err) {
      console.warn('API unavailable, using fallback data');
      const fallbackData = generateFallbackData();
      setData(fallbackData);
      setLatest(fallbackData[fallbackData.length - 1]);
      setCliData({
        cli_level: fallbackData[fallbackData.length - 1].cli_level,
        cli_mom: -0.8,
        cli_yoy: -5.2,
        categories: [
          { name: 'Housing', weight: 0.33, change: -0.8 },
          { name: 'Food', weight: 0.14, change: -0.3 },
          { name: 'Transport', weight: 0.16, change: -1.2 },
          { name: 'Healthcare', weight: 0.08, change: 0.4 },
          { name: 'Tech', weight: 0.12, change: -2.1 },
          { name: 'Services', weight: 0.10, change: -0.5 },
          { name: 'Energy', weight: 0.07, change: -0.9 },
        ]
      });
      setMeta({ model_version: 'ppf-v1.1.0', data_status: 'fallback' });
      setUsingFallback(true);
      setError('Using offline data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, latest, cliData, meta, loading, error, usingFallback, refresh: fetchData };
}

// =============================================================================
// COMPONENTS
// =============================================================================

const RegimeBadge = ({ regime }) => {
  const config = {
    deflation: { icon: TrendingUp, label: 'Deflation', color: 'bg-emerald-900/50 text-emerald-400 border-emerald-700' },
    inflation: { icon: TrendingDown, label: 'Inflation', color: 'bg-amber-900/50 text-amber-400 border-amber-700' },
    uncertain: { icon: AlertTriangle, label: 'Uncertain', color: 'bg-slate-800 text-slate-400 border-slate-600' },
  };
  const { icon: Icon, label, color } = config[regime] || config.uncertain;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm ${color}`}>
      <Icon size={14} />
      <span>{label}</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
        </p>
      ))}
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
  </div>
);

const StatusBanner = ({ usingFallback, meta, onRefresh }) => {
  if (!usingFallback) return null;
  
  return (
    <div className="bg-amber-900/20 border border-amber-800 rounded-lg px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-amber-400 text-sm">
        <AlertTriangle size={16} />
        <span>Using offline demo data. API connection unavailable.</span>
      </div>
      <button 
        onClick={onRefresh}
        className="text-amber-400 hover:text-amber-300 p-1"
      >
        <RefreshCw size={16} />
      </button>
    </div>
  );
};

// =============================================================================
// MAIN PANELS
// =============================================================================

const PPGPanel = ({ data, latest }) => {
  if (!data || !latest) return <LoadingSpinner />;
  
  const ma3 = data.slice(-3).reduce((sum, d) => sum + d.ppg, 0) / 3;
  const primaryDriver = Math.abs(latest.g_y_star) > Math.abs(latest.g_v) ? 'Productivity' : 'Velocity';
  
  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <p className="text-slate-500 text-xs mb-1">Latest PPG</p>
          <p className={`text-3xl font-bold ${latest.ppg >= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
            {latest.ppg >= 0 ? '+' : ''}{latest.ppg.toFixed(2)}%
          </p>
          <p className="text-slate-600 text-xs mt-1">
            CI: [{latest.ppg_p10.toFixed(1)}, {latest.ppg_p90.toFixed(1)}]
          </p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <p className="text-slate-500 text-xs mb-1">3-Month Avg</p>
          <p className={`text-2xl font-bold ${ma3 >= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
            {ma3 >= 0 ? '+' : ''}{ma3.toFixed(2)}%
          </p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <p className="text-slate-500 text-xs mb-1">Regime</p>
          <div className="mt-1">
            <RegimeBadge regime={latest.regime_flag} />
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <p className="text-slate-500 text-xs mb-1">Primary Driver</p>
          <p className="text-lg font-medium text-white">{primaryDriver}</p>
        </div>
      </div>

      {/* PPG Chart */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
        <h2 className="text-lg font-semibold text-white mb-4">
          PPG with 80% Confidence Band
        </h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ciGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="dateLabel" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} interval={5} />
              <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
              <Area type="monotone" dataKey="ppg_p90" stroke="none" fill="url(#ciGradient)" />
              <Area type="monotone" dataKey="ppg_p10" stroke="none" fill="#0f172a" />
              <Line type="monotone" dataKey="ppg_p50" stroke="#3b82f6" strokeWidth={2} dot={false} name="PPG" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 p-3 bg-slate-800/50 rounded-lg text-sm text-slate-400">
          <span className="text-blue-400 font-medium">Reading this chart:</span> The blue line is the PPG estimate. The shaded area shows 80% confidence—the true value likely falls within this band.
        </div>
      </div>
    </div>
  );
};

const DecompositionPanel = ({ latest }) => {
  if (!latest) return <LoadingSpinner />;
  
  const waterfallData = [
    { name: 'Productivity', value: latest.g_y_star, fill: '#10b981' },
    { name: 'Supply', value: -latest.g_m, fill: '#f59e0b' },
    { name: 'Velocity', value: -latest.g_v, fill: '#8b5cf6' },
    { name: 'PPG', value: latest.ppg, fill: latest.ppg >= 0 ? '#3b82f6' : '#ef4444' },
  ];
  
  const isProductivityDriven = Math.abs(latest.g_y_star) > Math.abs(latest.g_v);
  
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
      <h2 className="text-lg font-semibold text-white mb-2">PPG Decomposition</h2>
      <p className="text-slate-500 text-sm mb-4">What's driving purchasing power changes?</p>
      
      {/* Interpretation */}
      <div className={`p-4 rounded-lg mb-4 border-l-4 ${
        isProductivityDriven ? 'border-emerald-500 bg-emerald-900/20' : 'border-purple-500 bg-purple-900/20'
      }`}>
        <p className="text-sm text-slate-300">
          {isProductivityDriven ? (
            <>
              <strong>Productivity</strong> is the main driver (+{latest.g_y_star.toFixed(2)}%). 
              This is <span className="text-emerald-400">healthy deflation</span> from real efficiency gains.
            </>
          ) : latest.g_v < -2 ? (
            <>
              <strong>Velocity decline</strong> ({latest.g_v.toFixed(2)}%) is pushing PPG up. 
              <span className="text-amber-400"> Investigate</span> whether this is healthy saving or economic concern.
            </>
          ) : (
            <>
              <strong>Velocity surge</strong> (+{Math.abs(latest.g_v).toFixed(2)}%) is creating mild inflation. 
              This often signals <span className="text-blue-400">adoption activity</span>.
            </>
          )}
        </p>
      </div>

      {/* Waterfall Chart */}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={waterfallData} layout="vertical" margin={{ left: 80, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal vertical={false} />
            <XAxis type="number" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}%`} />
            <YAxis type="category" dataKey="name" stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine x={0} stroke="#64748b" />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {waterfallData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Formula */}
      <div className="mt-4 p-3 bg-slate-800/30 rounded-lg font-mono text-sm text-center text-slate-400">
        PPG = gY* − gM − gV = {latest.g_y_star.toFixed(2)} − {latest.g_m.toFixed(2)} − {latest.g_v.toFixed(2)} = 
        <span className={`ml-1 ${latest.ppg >= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
          {latest.ppg >= 0 ? '+' : ''}{latest.ppg.toFixed(2)}%
        </span>
      </div>
    </div>
  );
};

const CLIPanel = ({ data, cliData }) => {
  if (!data || !cliData) return <LoadingSpinner />;
  
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
      <h2 className="text-lg font-semibold text-white mb-2">BTC Cost-of-Living Index</h2>
      <p className="text-slate-500 text-sm mb-4">What does a basket of goods cost in satoshis?</p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <p className="text-slate-500 text-xs">Index Level</p>
          <p className="text-2xl font-bold text-white">{cliData.cli_level?.toFixed(1)}</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <p className="text-slate-500 text-xs">YoY Change</p>
          <p className={`text-xl font-bold ${(cliData.cli_yoy || 0) <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
            {(cliData.cli_yoy || 0) <= 0 ? '' : '+'}{(cliData.cli_yoy || 0).toFixed(1)}%
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <p className="text-slate-500 text-xs">Interpretation</p>
          <p className="text-sm text-slate-300 mt-1">
            {(cliData.cli_yoy || 0) < 0 ? 'Sats buy more' : 'Costs more sats'}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-48 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="dateLabel" stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} interval={8} />
            <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="cli_level" stroke="#f59e0b" strokeWidth={2} dot={false} name="CLI" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Categories */}
      {cliData.categories && (
        <>
          <p className="text-sm text-slate-400 mb-2">Category Breakdown</p>
          <div className="space-y-2">
            {cliData.categories.map((cat, i) => (
              <div key={cat.name} className="flex items-center gap-3 text-sm">
                <span className="w-20 text-slate-500">{cat.name}</span>
                <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full" 
                    style={{ width: `${cat.weight * 100}%`, backgroundColor: colors[i % colors.length] }} 
                  />
                </div>
                <span className={`w-14 text-right ${cat.change <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {cat.change <= 0 ? '' : '+'}{cat.change}%
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const LearnPanel = () => (
  <div className="space-y-6">
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
      <h2 className="text-lg font-semibold text-white mb-4">Understanding PPG Scenarios</h2>
      <p className="text-slate-400 text-sm mb-6">
        The same PPG number can mean very different things depending on what's driving it.
      </p>
      
      <div className="grid gap-4">
        {[
          { 
            name: 'Technology Boom', ppg: '+4.45%', driver: 'gY* = +5.0%', 
            signal: 'Healthy', color: 'emerald',
            desc: 'Productivity gains make goods cheaper. Real wealth creation. Sustainable deflation.'
          },
          { 
            name: 'Adoption Surge', ppg: '−6.05%', driver: 'gV = +8.0%', 
            signal: 'Growing Pains', color: 'amber',
            desc: 'More spending activity creates temporary inflation. Often signals Bitcoin adoption.'
          },
          { 
            name: 'HODLing Cycle', ppg: '+8.46%', driver: 'gV = −6.0%', 
            signal: 'Investigate', color: 'slate',
            desc: 'Velocity collapse. PPG is high but the cause may be concerning (fear, liquidity crisis).'
          },
        ].map((s) => (
          <div key={s.name} className={`p-4 rounded-lg border ${
            s.color === 'emerald' ? 'border-emerald-800 bg-emerald-900/20' :
            s.color === 'amber' ? 'border-amber-800 bg-amber-900/20' :
            'border-slate-700 bg-slate-800/30'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-medium text-white">{s.name}</p>
                <p className="text-slate-500 text-sm">{s.driver}</p>
              </div>
              <p className={`text-2xl font-bold ${
                s.color === 'emerald' ? 'text-emerald-400' :
                s.color === 'amber' ? 'text-amber-400' : 'text-slate-300'
              }`}>{s.ppg}</p>
            </div>
            <p className="text-sm text-slate-400">{s.desc}</p>
            <div className={`mt-2 inline-block px-2 py-1 rounded text-xs ${
              s.color === 'emerald' ? 'bg-emerald-900/50 text-emerald-400' :
              s.color === 'amber' ? 'bg-amber-900/50 text-amber-400' :
              'bg-slate-700 text-slate-400'
            }`}>{s.signal}</div>
          </div>
        ))}
      </div>
    </div>

    {/* About Section */}
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
      <h3 className="text-white font-medium mb-3">About This Framework</h3>
      <div className="space-y-3 text-sm text-slate-400">
        <p>
          <strong className="text-slate-300">What PPF does:</strong> Measures purchasing power 
          changes in a Bitcoin economy and decomposes them into productivity, supply, and velocity components.
        </p>
        <p>
          <strong className="text-slate-300">What PPF does NOT do:</strong> Predict BTC/USD price, 
          replace GDP/CPI for fiat economies, or claim deflation is inherently good.
        </p>
        <p>
          <strong className="text-slate-300">Core formula:</strong> PPG = gY* − gM − gV
        </p>
      </div>
    </div>
  </div>
);

// =============================================================================
// MAIN APP
// =============================================================================

export default function PPFDashboard() {
  const [activeTab, setActiveTab] = useState('ppg');
  const { data, latest, cliData, meta, loading, error, usingFallback, refresh } = usePPFData();

  const latestDate = latest?.date_month 
    ? (() => {
        const [year, month] = latest.date_month.split('-').map(Number);
        return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      })()
    : 'Loading...';

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-white">
                Bitcoin Purchasing Power Framework
              </h1>
              <p className="text-slate-500 text-sm">Economic measurement for a fixed-supply world</p>
            </div>
            <div className="text-right">
              <p className="text-slate-500 text-xs">Latest Data</p>
              <p className="text-white text-sm font-medium">{latestDate}</p>
              {meta && (
                <p className="text-slate-600 text-xs">{meta.model_version}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Quote Banner */}
      <div className="bg-gradient-to-r from-blue-950/50 via-slate-900 to-purple-950/50 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-5 text-center">
          <p className="text-lg text-slate-300 italic">
            "Not all 'number go up' is good. Not all 'number go down' is bad."
          </p>
          <p className="text-slate-500 text-sm mt-1">This framework helps you tell the difference.</p>
        </div>
      </div>

      {/* Status Banner */}
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <StatusBanner usingFallback={usingFallback} meta={meta} onRefresh={refresh} />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-800 bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'ppg', label: 'PPG Overview' },
              { id: 'decomp', label: 'Decomposition' },
              { id: 'cli', label: 'Cost of Living' },
              { id: 'learn', label: 'Learn' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {activeTab === 'ppg' && <PPGPanel data={data} latest={latest} />}
            {activeTab === 'decomp' && <DecompositionPanel latest={latest} />}
            {activeTab === 'cli' && <CLIPanel data={data} cliData={cliData} />}
            {activeTab === 'learn' && <LearnPanel />}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between text-xs text-slate-600">
          <span>PPF Model {meta?.model_version || 'v1.1.0'} • Educational purposes only</span>
          <span>Not financial advice</span>
        </div>
      </footer>
    </div>
  );
}
